import React, { useState, useEffect } from "react";
import { Steps, Button, message, Spin } from "antd";
import Papa from "papaparse";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import UploadData from "./UploadData";
import ViewResults from "./ViewResult";
import FinalizeDependentVariables from "./FinalizeDependentVariables";
import { TableRecord } from "../../../../components/Table";
import axiosInstance from "../../../../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { getTenantId } from "../../../../utils/auth";
import { DataStructure } from "../../../../models/interfaces";
import {
  VARIABLE_SUMMARY_API,
  SEASONALITY_API,
  SIMILARITY_MATRIX_API,
  CORRELATION_MATRIX_API,
  VARIATION_INFLATION_FACTOR_API,
} from "../../../../constants/API";
import MMMScenarioPlanner from "./MMMScenarioPlanner";
import { uploadFileToS3 } from "../../../../utils/s3Upload";

dayjs.extend(customParseFormat);

const MMMJobsCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetchingJobDetails, setFetchingJobDetails] = useState(false);
  const query = new URLSearchParams(useLocation().search);
  const [fileUploaded, setFileUploaded] = useState(false);
  const jobConfigId = query.get("jobConfigId");
  const [jobId, setJobId] = useState<string | null>(jobConfigId);
  const [current, setCurrent] = useState(0);
  const [name, setName] = useState<string | null>("");
  const [dataType, setDataType] = useState<string>("sales-model");
  const [file, setFile] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const isViewOnly = location.state?.viewOnly === true;
  const [trigger, setTrigger] = useState<boolean>(false);
  const [dependentVariables, setDependentVariables] = useState<any>([]);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [IsBusiness, setIsBusiness] = useState<string>("IsBusinessNot");
  const [seasonDataSet, setSeasonDataSet] = useState<any[]>([]);
  const [seasonLabel, setSeasonLabel] = useState<any[]>([]);
  const [Variable, setVariable] = useState<any[]>([]);
  const [spendVariables, setSpendVariables] = useState<any[]>([]);
  const [columnMappings, setColumnMappings] = useState<{
    dateColumn: string;
    locationColumn: string;
    kpiColumn: string;
  }>({
    dateColumn: "date",
    locationColumn: "location",
    kpiColumn: "y",
  });
  const [variableSummaryStatistic, setVariableSummaryStatistic] =
    useState<any[]>();
  const [multiColinearityRecords, setMultiColinearityRecords] = useState<
    TableRecord[]
  >([]);
  const [heatMapData, setHeatMapData] = useState<any>(undefined);
  const [allVariiables, setAllVariiables] = useState<DataStructure>({
    spendVariable: {
      title: "Spend Variable",
      subtitle: "Select the spend variable",
      items: [],
    },
    impressionVariable: {
      title: "Impression Variable",
      subtitle: "Select the impression variable",
      items: [],
    },
    nonMediaVariable: {
      title: "Non-Media Variable",
      subtitle: "Select the non-media variable",
      items: [],
    },
  });
  const [metaFields, setMetaFields] = useState<string[]>([]); // Store meta.fields

  const handleNextButtonClick = () => {
    // if(current === 0) {
    //   fetchChartData()
    // }
    setTrigger((prev) => !prev);
  };

  const submitFeatureForm = async () => {
    try {
      setLoading(true);

      // Validate that we have media variables
      const mediaVariables = allVariiables.spendVariable.items
        .filter((item: any) => dependentVariables.includes(item.label))
        .map((item: any) => item.label);

      if (!mediaVariables || mediaVariables.length === 0) {
        message.error("At least one spend variable (media variable) is required for MMM. Please categorize your spend columns in the 'Spend Variable' section.");
        setLoading(false);
        return;
      }

      // Only upload file if not in edit mode
      if (!isEditMode && file) {
        const mmmFilePath = jobId + "/input/mmm_data.csv";
        await uploadFileToS3(file, mmmFilePath);
      }

      const payload = {
        jobConfigId: jobId,
        name: name,
        jobType: "MMM",
        status: "RUNNING",
        tenantId: getTenantId(),
        dataSourceConfig: {
          dataSourceType: "FILE_CSV",
        },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            mmm: {
              columnMapping: {
                media_variables: allVariiables.spendVariable.items
                  .filter((item: any) => dependentVariables.includes(item.label))
                  .map((item: any) => item.label),
                non_media_variables: allVariiables.nonMediaVariable.items
                  .filter((item: any) => dependentVariables.includes(item.label))
                  .map((item: any) => item.label),
                impression_variables: allVariiables.impressionVariable.items
                  .filter((item: any) => dependentVariables.includes(item.label))
                  .map((item: any) => item.label),
                target_variables: ["Sales"],
                date_variables: ["Date"],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            dateFormat: "%m/%d/%Y",
            isSeasonal: IsBusiness === "IsBusinessYes",
            modelType: dataType,
          },
        },
        modelOutputConfig: {},
      };

      // Debug logging for payload
      console.log("MMM Job Payload:", JSON.stringify(payload, null, 2));
      console.log("Media Variables:", payload.dataMappingConfig.mappings.mmm.columnMapping.media_variables);
      console.log("Impression Variables:", payload.dataMappingConfig.mappings.mmm.columnMapping.impression_variables);
      console.log("Non-Media Variables:", payload.dataMappingConfig.mappings.mmm.columnMapping.non_media_variables);

      // Use PUT for edit mode, POST for create mode
      if (isEditMode) {
        await axiosInstance.post(`/api/jobConfig`, payload);
        message.success("Job updated successfully");
      } else {
        await axiosInstance.post("/api/jobConfig", payload);
        message.success("Job created successfully");
      }

      // Run the job
      await axiosInstance.post(
        "/api/jobRun",
        {
          jobConfigId: jobId,
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );
      navigate("/planning/marketing-mix-model-landing");
      setCurrent(2);
      setLoading(false);
    } catch (error) {
      console.error("Error in submitFeatureForm:", error);
      message.error("Failed to save job configuration");
      setLoading(false);
    }
  };
  // Fetch and parse the CSV from S3. Returns the File so callers can pass it
  // directly to fetchChartData, avoiding a second S3 download.
  // fetchingJobDetails state is managed by the caller (fetchJobDetails).
  const fetchFileDetails = async (id: string): Promise<File | null> => {
    try {
      const response = await axiosInstance.get(
        `/api/s3/getFile?path=${id}/input/mmm_data.csv`
      );
      const csvContent = response.data;

      const blob = new Blob([csvContent], { type: "text/csv" });
      const csvFile = new File([blob], "mmm_data.csv", { type: "text/csv" });

      setFile(csvFile);
      setIsEditMode(true);
      setFileUploaded(true);

      Papa.parse(csvContent, {
        header: true,
        complete: (result) => {
          const { data, meta } = result;
          setMetaFields(meta.fields || []);

          const autoDetectedColumns = {
            dateColumn:
              meta.fields?.find((f) => f.toLowerCase().includes("date")) ||
              "date",
            locationColumn:
              meta.fields?.find((f) => f.toLowerCase().includes("location")) ||
              "location",
            kpiColumn:
              meta.fields?.find((f) =>
                f.toLowerCase().match(/y|sales|revenue/)
              ) || "y",
          };

          setColumnMappings(autoDetectedColumns);

          const parsedData = data
            .filter(
              (item: any) =>
                item[autoDetectedColumns.dateColumn] &&
                item[autoDetectedColumns.locationColumn]
            )
            .map((item: any) => ({
              date: item[autoDetectedColumns.dateColumn],
              location: item[autoDetectedColumns.locationColumn].trim(),
              value: parseFloat(item[autoDetectedColumns.kpiColumn]) || 0,
            }));

          setCsvData(parsedData);
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          setCsvData([]);
        },
      });

      return csvFile;
    } catch (error) {
      console.error("Error fetching file details:", error);
      message.error("Failed to fetch file details");
      navigate("/planning/marketing-mix-model-landing");
      return null;
    }
  };
  const fetchJobDetails = async (id: string) => {
    try {
      setFetchingJobDetails(true);

      // First fetch the job configuration
      const response = await axiosInstance.get(`/api/jobConfig/${id}`);
      const jobData = response.data;

      // Set job details from the API response
      // setName(jobData.name);
      setJobId(jobData.jobConfigId);
      setIsEditMode(true);
      setFileUploaded(true); // Since we're in edit mode, we don't need to upload a file again

      // Set model type
      if (jobData.modelParameterConfig?.parameters?.modelType) {
        setDataType(jobData.modelParameterConfig.parameters.modelType);
        setVariable(jobData.modelParameterConfig.parameters.modelType);
      }

      // Set seasonality
      if (
        jobData.modelParameterConfig?.parameters?.isSeasonal === true ||
        jobData.modelParameterConfig?.parameters?.isSeasonal === "true"
      ) {
        setIsBusiness("IsBusinessYes");
      } else {
        setIsBusiness("IsBusinessNot");
      }

      // Set dependent variables from the mapping config
      const mappings = jobData.dataMappingConfig?.mappings?.mmm?.columnMapping;
      if (mappings) {
        const allVars = [
          ...(mappings.media_variables || []),
          ...(mappings.impression_variables || []),
          ...(mappings.non_media_variables || []),
        ];
        setDependentVariables(allVars);

        // Create items for allVariiables
        const spendItems = (mappings.media_variables || []).map(
          (label: string, index: number) => ({
            id: index + 1,
            label,
            checked: true, // Mark as checked since these are already selected
          })
        );

        const impressionItems = (mappings.impression_variables || []).map(
          (label: string, index: number) => ({
            id: index + 1 + spendItems.length,
            label,
            checked: true, // Mark as checked since these are already selected
          })
        );

        const nonMediaItems = (mappings.non_media_variables || []).map(
          (label: string, index: number) => ({
            id: index + 1 + spendItems.length + impressionItems.length,
            label,
            checked: true, // Mark as checked since these are already selected
          })
        );

        // Update allVariiables with the items from the API
        setAllVariiables({
          spendVariable: {
            title: "Spend Variable",
            subtitle: "Select the spend variable",
            items: spendItems,
          },
          impressionVariable: {
            title: "Impression Variable",
            subtitle: "Select the impression variable",
            items: impressionItems,
          },
          nonMediaVariable: {
            title: "Non-Media Variable",
            subtitle: "Select the non-media variable",
            items: nonMediaItems,
          },
        });

        // Fetch the CSV once; pass it to fetchChartData to avoid a second S3 download.
        const csvFile = await fetchFileDetails(id);

        // Fetch chart data for Finalize step (uses the already-loaded csvFile).
        if (csvFile) {
          await fetchChartData(id, spendItems, impressionItems, nonMediaItems, csvFile);
        }
      }

      setFetchingJobDetails(false);
    } catch (error) {
      console.error("Error fetching job details:", error);
      message.error("Failed to fetch job details");
      setFetchingJobDetails(false);

      // If there's an error, navigate back to the list page
      navigate("/planning/marketing-mix-model-landing");
    }
  };

  // Function to fetch chart data for edit/view mode.
  // Accepts a pre-loaded csvFile to avoid a duplicate S3 download when called
  // from fetchJobDetails (which already fetched the file via fetchFileDetails).
  const fetchChartData = async (
    jobId: string,
    spendItems: any[],
    impressionItems: any[],
    nonMediaItems: any[],
    preloadedFile?: File
  ) => {
    try {
      setLoading(true);

      try {
        // Use the pre-loaded file when available; only fall back to S3 fetch
        // when called in a standalone context (e.g. future use-cases).
        let csvFile: File;
        if (preloadedFile) {
          csvFile = preloadedFile;
        } else {
          const fileResponse = await axiosInstance.get(
            `/api/s3/getFile?path=${jobId}/input/mmm_data.csv`,
            { responseType: "blob" }
          );
          csvFile = new File([fileResponse.data], "mmm_data.csv", {
            type: "text/csv",
          });
        }

        // 2. Fetch seasonality data
        const seasonalityFormData = new FormData();
        seasonalityFormData.append("csv_file", csvFile);
        seasonalityFormData.append("date_column", "Date");
        seasonalityFormData.append("response_variable_column", "Sales"); // Assuming Sales is the response variable
        seasonalityFormData.append("yearly_seasonality", "true");

        const seasonalityResponse = await axiosInstance.post(
          SEASONALITY_API,
          seasonalityFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );

        if (
          seasonalityResponse.data &&
          seasonalityResponse.data.seasonality_data
        ) {
          const seasonalityData = seasonalityResponse.data.seasonality_data;
          const labels = seasonalityData.map((item: any) => item["Date"]);
          const seasonData = seasonalityData.map((item: any) => item.Season);

          setSeasonLabel(labels);
          setSeasonDataSet([
            {
              name: "Season",
              data: seasonData,
              borderColor: "rgba(75, 192, 192, 1)",
              backgroundColor: "rgba(75, 192, 192, 0.3)",
            },
          ]);
        }

        // 3. Fetch variable summary statistics
        const variableSummaryFormData = new FormData();
        variableSummaryFormData.append("csv_file", csvFile);
        variableSummaryFormData.append("date_column", "Date");
        variableSummaryFormData.append("date_format", "%m/%d/%Y");

        // Add all variables to the form
        [...spendItems, ...impressionItems, ...nonMediaItems].forEach(
          (item: any) => {
            variableSummaryFormData.append("columns", item.label);
          }
        );

        const variableSummaryResponse = await axiosInstance.post(
          VARIABLE_SUMMARY_API,
          variableSummaryFormData
        );

        if (
          variableSummaryResponse.data &&
          variableSummaryResponse.data.variable_summary
        ) {
          setVariableSummaryStatistic(
            variableSummaryResponse.data.variable_summary
          );
        }

        // 4. Fetch correlation matrix
        const correlationMatrixForm = new FormData();
        correlationMatrixForm.append("csv_file", csvFile);
        correlationMatrixForm.append("target_variable", "Sales");
        correlationMatrixForm.append("response_variable_column", "Sales");

        // Add all variables to the form
        spendItems.forEach((item: any) => {
          correlationMatrixForm.append("spend_variables", item.label);
        });

        nonMediaItems.forEach((item: any) => {
          correlationMatrixForm.append("non_media_variables", item.label);
        });

        impressionItems.forEach((item: any) => {
          correlationMatrixForm.append("impression_variables", item.label);
        });

        const correlationMatrixResponse = await axiosInstance.post(
          CORRELATION_MATRIX_API,
          correlationMatrixForm,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );

        if (
          correlationMatrixResponse.data &&
          correlationMatrixResponse.data.correlation_matrix
        ) {
          const correlationMatrix =
            correlationMatrixResponse.data.correlation_matrix;
          const xLabels = Object.keys(correlationMatrix);
          const yLabels = xLabels;
          const zData = xLabels.map((x) =>
            yLabels.map((y) => correlationMatrix[x][y])
          );

          setHeatMapData({
            xLabels,
            yLabels,
            zData,
          });
        }

        // 5. Fetch multi-collinearity data
        const multiColinearityForm = new FormData();
        multiColinearityForm.append("csv_file", csvFile);

        // Add all variables to the form
        spendItems.forEach((item: any) => {
          multiColinearityForm.append("spend_variables", item.label);
        });

        nonMediaItems.forEach((item: any) => {
          multiColinearityForm.append("non_media_variables", item.label);
        });

        impressionItems.forEach((item: any) => {
          multiColinearityForm.append("impression_variables", item.label);
        });

        const multiColinearityResponse = await axiosInstance.post(
          VARIATION_INFLATION_FACTOR_API,
          multiColinearityForm,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );

        if (multiColinearityResponse.data) {
          let responseData = multiColinearityResponse.data;

          // Handle string response
          if (typeof responseData === "string") {
            try {
              responseData = JSON.parse(
                responseData.replaceAll("Infinity", "1")
              );
            } catch (error) {
              console.error("Failed to parse JSON string:", error);
            }
          }

          // Process the data
          if (responseData.variance_inflation_factors) {
            const records: TableRecord[] = [];
            Object.keys(responseData.variance_inflation_factors).forEach(
              (key) => {
                records.push({
                  variable: key,
                  value: responseData.variance_inflation_factors[key],
                });
              }
            );

            setMultiColinearityRecords(records);
          }
        }

        setLoading(false);
      } catch (innerError) {
        console.error("Error fetching or processing file:", innerError);
        message.warning(
          "Could not load all chart data. Some visualizations may be missing."
        );
        // Continue with the edit mode even if chart data couldn't be loaded
        setLoading(false);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      message.error("Failed to fetch chart data for edit mode");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryJobConfigId = query.get("jobConfigId");
    const stateJobConfigId = location.state?.jobConfigId;

    if (queryJobConfigId || stateJobConfigId) {
      const resolvedJobId = queryJobConfigId ?? stateJobConfigId;
      setJobId(resolvedJobId);

      if (queryJobConfigId) {
        // Edit flow via URL — start at step 1 and load all data
        fetchJobDetails(queryJobConfigId);
        setCurrent(0);
      } else if (stateJobConfigId) {
        // View flow from listing page — load all job data so every step
        // has the information it needs; the target step is applied below.
        fetchJobDetails(stateJobConfigId);
      }
    } else {
      setJobId(uuidv4()); // new job
    }

    if (location.state?.jobName) {
      setName(location.state.jobName);
    }
    if (location.state?.step !== undefined) {
      setCurrent(location.state.step);
    }
  }, [location]);
  const steps = [
    {
      title: "Upload  data",
      description: "Historic data upload",
      content: (
        <div>
          <UploadData
            setFileUploaded={setFileUploaded}
            setVariableSummaryStatistic={setVariableSummaryStatistic}
            setMultiColinearityRecords={setMultiColinearityRecords}
            setHeatMapData={setHeatMapData}
            setCurrent={setCurrent}
            current={current}
            setAllVariiables={setAllVariiables}
            setName={setName}
            name={name}
            setFile={setFile}
            trigger={trigger}
            setTrigger={setTrigger}
            setLoading={setLoading}
            file={file}
            setIsBusiness={setIsBusiness}
            IsBusiness={IsBusiness}
            setDataType={setDataType}
            dataType={dataType}
            setSeasonLabel={setSeasonLabel}
            seasonLabel={seasonLabel}
            seasonDataSet={seasonDataSet}
            setSeasonDataSet={setSeasonDataSet}
            isEditMode={isEditMode}
            isViewOnly={isViewOnly}
            variable={Variable}
            loading={loading}
          />
        </div>
      ),
    },
    {
      title: "Finalize dependent Variables",
      description: "Take informed decision using various data points  ",
      content: (
        <div>
          <FinalizeDependentVariables
            csvData={csvData}
            columnMappings={columnMappings}
            metaFields={metaFields}
            variableSummaryStatistic={variableSummaryStatistic}
            multiColinearityRecords={multiColinearityRecords}
            heatMapData={heatMapData}
            submitFeatureForm={submitFeatureForm}
            allVariiables={allVariiables}
            setDependentVariables={setDependentVariables}
            dependentVariables={dependentVariables}
            seasonLabel={seasonLabel}
            seasonDataSet={seasonDataSet}
            isEditMode={isEditMode}
            isViewOnly={isViewOnly}
            loading={loading}
          />
        </div>
      ),
    },

    {
      title: "View Results",
      description: "Measure impact on KPI - Channel-Wise Contributions",
      content: (
        <ViewResults
          setSpendVariables={setSpendVariables}
          jobId={jobId}
          csvData={csvData}
        />
      ),
    },
    {
      title: "Scenario Planner",
      description: "AI-driven budget and KPI optimization",
      content: <MMMScenarioPlanner jobId={jobId} variables={spendVariables} />,
    },
  ];
  const handlePrev = () => {
    setCurrent((current) => Math.max(current - 1, 0)); // Ensure it doesn't go below 0
  };
  const handleNext = () => {
    // View-only mode: just navigate through steps, no validation or API calls
    if (isViewOnly) {
      if (current < steps.length - 1) {
        setCurrent(current + 1);
      } else {
        navigate("/planning/marketing-mix-model-landing");
      }
      return;
    }

    if (current === 0) {
      if (!name || name.trim().length === 0) {
        message.error("Please enter a name for the job");
        return;
      } else if (!fileUploaded && !isEditMode) {
        message.error("Please upload a CSV file");
        return;
      }

      // In edit mode, we can skip file upload validation and directly move to step 1
      if (isEditMode) {
        setCurrent(1);
      } else {
        handleNextButtonClick();
      }
    } else if (current === 1) {
      // setCurrent(2);
      submitFeatureForm();
    } else if (current === 2) {
      setCurrent(3);
    } else if (current === 3) {
      navigate("/planning/marketing-mix-model-landing");
    }
  };
  return (
    <div className="w-full h-[calc(100vh-75px)] flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {fetchingJobDetails ? (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" tip="Loading job details..." />
        </div>
      ) : (
        <>
          <div className="flex flex-1 h-full overflow-hidden">
            <div
              className="w-1/4 pr-4 ml-6 overflow-y-auto"
              style={{ height: "calc(100vh - 120px)" }}
            >
              <div
                style={{ zIndex: 100 }}
                className="sticky top-0 pb-1 bg-white px-4"
              >
                <h2
                  title={name || ""}
                  className="text-2xl  pt-3 font-semibold mb-4 text-gray-900 max-w-[300px] truncate"
                >
                  {name || "Marketing Mix Model Test"}
                </h2>
              </div>

              <Steps
                direction="vertical"
                current={current}
                onChange={(e) => {
                  // setCurrent(e);
                }}
                items={steps.map((step) => ({
                  title: step.title,
                  description: step.description,
                  disabled: true,
                }))}
                className="[&_.ant-steps-item]:mb-16 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[110px] [&_.ant-steps-item-tail]:!w-[4px] [&_.ant-steps-item-icon]:!text-2xl [&_.ant-steps-item-icon]:!w-10 [&_.ant-steps-item-icon]:!h-10 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
              />
            </div>
            <div className="w-3/4 border-l overflow-y-auto px-10 py-7 pb-10">
              {steps[current].content}
            </div>
          </div>
          <div className="flex justify-end items-center bg-white border-t p-4">
            {current > 0 && (
              <Button onClick={handlePrev} size="large">
                Previous
              </Button>
            )}
            {current <= steps.length - 1 ? (
              <Button
                type="primary"
                size="large"
                loading={loading}
                className="ml-auto"
                onClick={handleNext}
              >
                {current === steps.length - 1 ? "Finish" : "Next"}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default MMMJobsCreate;
